import type { ComponentProps, ReactNode } from 'react';
import type { ButtonProps } from '../Button/Button';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import Button from '../Button/Button';

export type PopoverPanelAnchorTo = Extract<
  NonNullable<ComponentProps<typeof PopoverPanel>['anchor']>,
  string
>;

interface PopoverComponentProps {
  /**
   * Popover button
   */
  popoverButton?: ReactNode;
  /**
   * Preferred popover panel anchor with automatic collision handling
   */
  popoverPanelAnchorTo?: PopoverPanelAnchorTo;
  /**
   * Popover content
   */
  content: ReactNode;
  /**
   * Popover button props
   */
  popoverButtonProps?: Omit<ButtonProps, 'ref'> & {
    isOurButton?: boolean;
  };
  /**
   * Additional CSS classes for component wrapper
   */
  additionalClasses?: string;
  /**
   * Additional CSS classes for popover panel
   */
  additionalPopoverPanelClasses?: string;
  /**
   * Component test id
   */
  testId?: string;
}

const PopoverComponent = ({
  popoverButton,
  popoverPanelAnchorTo = 'right start',
  content = EMPTY_ARRAY,
  additionalClasses = '',
  popoverButtonProps,
  additionalPopoverPanelClasses = '',
  testId,
}: PopoverComponentProps) => {
  const popoverButtonBaseProps = useMemo(() => {
    const { isOurButton, ...rest } = popoverButtonProps || {};
    return {
      ...rest,
      ...(isOurButton ? { as: Button } : {}),
    };
  }, [popoverButtonProps]);

  return (
    <Popover
      className={twMerge('relative w-fit', additionalClasses)}
      {...getTestProps(testId, 'container')}
    >
      <PopoverButton
        className="focus:outline-hidden focus-visible:outline-hidden cursor-pointer"
        {...getTestProps(testId, 'button')}
        {...popoverButtonBaseProps}
      >
        {popoverButton}
      </PopoverButton>
      <PopoverPanel
        anchor={{
          to: popoverPanelAnchorTo,
          gap: 12,
          padding: 16,
        }}
        className={twMerge(
          'z-20 px-0 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-200 shadow-lg rounded-lg',
          'absolute border-2 border-slate-200 dark:border-slate-700 overflow-visible',
          'transition-[opacity,translate] ease-out duration-200 data-closed:opacity-0 data-closed:translate-y-1',
          'z-20',
          additionalPopoverPanelClasses,
        )}
        transition
        {...getTestProps(testId, 'popover')}
      >
        {content}
      </PopoverPanel>
    </Popover>
  );
};

export default PopoverComponent;
